import React from "react";
import styled from "styled-components";
import { ReactComponent as SoccerBallIcon } from "assets/icons/match-log/soccer-ball.svg";
import { getMatchLogTypeVisual } from "../matchLogIcons";
import { MATCH_LOG_TIME_KIND_ICON_COMPONENTS } from "../matchLogTimeKindAssets";
import type { MatchLogEventType, MatchLogGoalOutcome, MatchLogTimeKind } from "../types";

type MatchLogTypeIconProps = {
  type: MatchLogEventType;
  goalOutcome?: MatchLogGoalOutcome;
  timeKind?: MatchLogTimeKind;
  size?: number;
  /** Nadpisuje kolor ikony; `'inherit'` używa `currentColor` z rodzica (np. hover w CSS). */
  color?: string | "inherit";
};

/** Ikona typu zdarzenia — kartki jako pionowy prostokąt (jak w meczu). */
const MatchLogTypeIcon: React.FC<MatchLogTypeIconProps> = ({
  type,
  goalOutcome,
  timeKind,
  size = 18,
  color,
}) => {
  const visual = getMatchLogTypeVisual(type, goalOutcome);
  const iconColor = color === "inherit" ? "currentColor" : color ?? visual.iconColor;

  if (timeKind) {
    const TimeKindIcon = MATCH_LOG_TIME_KIND_ICON_COMPONENTS[timeKind];

    return (
      <TimeKindIconWrap $size={size} $color={iconColor} aria-hidden>
        <TimeKindIcon />
      </TimeKindIconWrap>
    );
  }

  if (type === "yellowCard" || type === "redCard") {
    const cardHeight = size;
    const cardWidth = Math.round(size * 0.62);
    return (
      <CardShape
        style={{
          width: cardWidth,
          height: cardHeight,
          backgroundColor: iconColor,
          borderRadius: Math.max(2, Math.round(size * 0.12)),
        }}
      />
    );
  }

  const stroke = iconColor;
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none" as const };

  if (visual.icon === "soccer") {
    return (
      <SoccerIconWrap $size={size} $color={iconColor} aria-hidden>
        <SoccerBallIcon />
      </SoccerIconWrap>
    );
  }

  if (visual.icon === "minusCircle") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" stroke={stroke} strokeWidth="1.8" />
        <path d="M7.5 12h9" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (visual.icon === "swap") {
    return (
      <svg {...common}>
        <path
          d="M7 8h11M15.5 5.5 18 8l-2.5 2.5M17 16H6M8.5 13.5 6 16l2.5 2.5"
          stroke={stroke}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (visual.icon === "clock") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" stroke={stroke} strokeWidth="1.8" />
        <path d="M12 7.5V12l3 2" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path
        d="M7 4.5h8.5L19 7.5V19.5H7z"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 9h7M9.5 12.5h7M9.5 16h4.5"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default MatchLogTypeIcon;

const CardShape = styled.span`
  display: inline-block;
  flex-shrink: 0;
`;

const SoccerIconWrap = styled.span<{ $size: number; $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  color: ${({ $color }) => $color};

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  path {
    fill: currentColor;
  }
`;

const TimeKindIconWrap = styled.span<{ $size: number; $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  flex-shrink: 0;
  color: ${({ $color }) => $color};

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;
